/**
 * Datos de la fila de documento de la dirección v2 (DESIGN.md §11.2): título, frase, columna de datos, señal y
 * agrupación por fecha. Una sola fuente para las cuatro bibliotecas y Favoritos.
 *
 * Mientras no exista la frase generada (etapa E2, §11.5), la frase es el criterio guardado en el resumen IA, que la
 * fila recorta a dos líneas. Nada aquí inventa contenido: sin dato, la zona no se muestra.
 */
import type { Document } from './supabase';
import type { CollectionKey } from './institutions';
import { parseAiSummary, stripJsonArtifacts } from './formatters';
import { numeroReal, tipoSingular, tituloDocumento } from './bibliotecas';
import { citaDocumento, doctrinaPrevia } from './fichaDocumento';
import { tribunalDeRit } from './tribunales';
import { estadoConvenio, etiquetaNorma, materiasNorma, paisConvenio, tituloConvenio, tituloNorma } from './normasFila';
import { fechaCorta } from './fechas';

/** Campos que la fila usa; Favoritos y las bibliotecas traen distintos subconjuntos de `Document`. */
export type DocFila = Pick<Document, 'id' | 'institution_code' | 'tipo' | 'titulo'> & {
  numero?: string | null;
  year?: number | null;
  fecha_publicacion?: string | null;
  resumen?: string | null;
  ai_summary?: string;
  contenido_extraido?: string;
  /** Leyes: materias separadas por coma. */
  materia?: string | null;
  /** Convenios: país, clase y vigencia en `legislacion[0]`. */
  legislacion?: unknown;
};

const esNorma = (doc: DocFila) => (doc.institution_code || '').toUpperCase() === 'BCN';
const esConvenio = (doc: DocFila) => (doc.tipo || '').toLowerCase() === 'convenio';

/** Caracteres que caben en una línea de 700 px a 15,5/600 (medido en la maqueta m1: ~84). */
export const LARGO_TITULO = 80;

const SIN_RESUMEN = 'sin resumen disponible';
const normal = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
const limpio = (v: unknown): string => (typeof v === 'string' ? stripJsonArtifacts(v).replace(/\s+/g, ' ').trim() : '');

/**
 * Título de la fila: el oficial si cabe en una línea; si no, el título breve del resumen IA (`titulo`). Sin título
 * breve, el oficial (la fila lo corta con elipsis).
 */
export function tituloFila(doc: DocFila, conPais = true): string {
  // Leyes: el oficial en tipo oración (§11.6). Convenios: la clase, con el país delante salvo cuando la lista ya
  // agrupa por país (el país va en el rótulo del grupo).
  if (esNorma(doc)) return tituloNorma(doc);
  if (esConvenio(doc)) return tituloConvenio(doc, conPais);
  const oficial = limpio(tituloDocumento({ ...doc, resumen: doc.resumen || '' }) || doc.titulo);
  if (oficial && oficial.length <= LARGO_TITULO) return oficial;
  const breve = limpio(parseAiSummary(doc.ai_summary, doc.resumen).titulo);
  return breve || oficial;
}

/** Campo del resumen IA que hace de frase por tipo (luego `resumen`). */
const CAMPO_FRASE: Record<string, string> = {
  oficio: 'criterio_aplicable',
  circular: 'criterio_principal',
  resolucion: 'objeto',
  'resolucion exenta': 'objeto',
};

export interface Frase {
  texto: string;
  /** true si no hay síntesis y la frase es el inicio del texto del documento (marca «sin síntesis»). */
  sinSintesis: boolean;
  /** Ley sin síntesis: la frase son sus materias (§11.6). */
  materias?: boolean;
}

/**
 * Frase de la fila. Orden: el criterio del tipo, el `resumen` del resumen IA y el `resumen` en texto plano. Se salta
 * lo que repite el título (en resoluciones el título ya es el `objeto`). Sin nada de eso, el inicio del texto del
 * documento con la marca «sin síntesis»; sin texto, null.
 */
export function fraseFila(doc: DocFila): Frase | null {
  const r = parseAiSummary(doc.ai_summary, doc.resumen);
  const tipo = (doc.tipo || '').toLowerCase();
  const titulo = normal(tituloFila(doc));
  const plano = doc.resumen && !doc.resumen.trim().startsWith('{') ? doc.resumen : '';
  // El resumen de los convenios termina con la fuente o con «Decreto N° S/N.»: no son frase.
  const candidatos = [CAMPO_FRASE[tipo] ? r[CAMPO_FRASE[tipo]] : '', r.resumen, plano].map(limpio)
    .map(t => t.replace(/\s*Fuente: SII, Convenios Internacionales\.?$/i, '').replace(/\s*Decreto N° S\/N\.?$/i, ''));
  const texto = candidatos.find(t => t && normal(t) !== SIN_RESUMEN && normal(t) !== titulo);
  // Una ley trae un resumen largo: la fila muestra su primera oración, completa (§11.2, m5).
  if (texto) return { texto: esNorma(doc) ? primeraOracion(texto) : texto, sinSintesis: false };
  if (esNorma(doc)) {
    const materias = materiasNorma(doc);
    if (materias.length) return { texto: materias.join(' · '), sinSintesis: true, materias: true };
  }
  const inicio = limpio(doc.contenido_extraido).slice(0, 320);
  return inicio ? { texto: inicio, sinSintesis: true } : null;
}

/** Primera oración de un resumen largo (40 a 320 caracteres); si no se puede cortar, el texto entero. */
export function primeraOracion(t: string): string {
  // El punto que cierra la oración sigue a una minúscula, un número o un paréntesis: «D.L. N° 824» no corta.
  const m = /^(.{40,320}?[a-záéíóúñ0-9)%][.;])\s+[A-ZÁÉÍÓÚÑ¿«"]/.exec(t);
  return m ? m[1] : t;
}

/** true si el documento declara que cambia un criterio y nombra el documento afectado (información, en gris). */
export function cambiaCriterio(doc: DocFila): boolean {
  return doctrinaPrevia(parseAiSummary(doc.ai_summary, doc.resumen).cambio_criterio) !== null;
}

const MES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
const MES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** Fecha de calendario de la base («2026-10-14» o un instante): año, mes (1-12) y día, sin correrse de día. */
function partes(fecha: string | null | undefined): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(fecha || '');
  if (!m) return null;
  const p = { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
  return p.m >= 1 && p.m <= 12 && p.d >= 1 && p.d <= 31 ? p : null;
}

/** «14 oct» si es del año en curso; «14 oct 2019» si no. */
export function diaFila(fecha: string | null | undefined, ahora: Date = new Date()): string {
  const p = partes(fecha);
  if (!p) return '';
  return p.y === ahora.getFullYear() ? `${p.d} ${MES_CORTO[p.m - 1]}` : `${p.d} ${MES_CORTO[p.m - 1]} ${p.y}`;
}

export interface Datos {
  /** Línea fuerte: «Oficio 2752», tribunal. */
  a: string;
  /** Línea suave: fecha o «RIT … · fecha». */
  b: string;
  /** Información en gris bajo la fecha («Cambia un criterio»); null si no aplica. */
  nota: string | null;
}

/** Columna de datos según la biblioteca (§11.2): SII «Oficio N» y fecha; TTA tribunal y «RIT · fecha». */
export function datosFila(doc: DocFila, coleccion: CollectionKey, ahora: Date = new Date()): Datos {
  // Leyes (§11.6): «Ley 21.850» y fecha de publicación. Convenios: «Vigente desde», «Suscrito» o «Publicado» y fecha.
  if (coleccion === 'ley' || esNorma(doc)) return { a: etiquetaNorma(doc), b: fechaCorta(doc.fecha_publicacion), nota: null };
  if (coleccion === 'conv' || esConvenio(doc)) {
    const e = estadoConvenio(doc);
    return { a: e.rotulo, b: fechaCorta(e.fecha), nota: null };
  }
  const n = numeroReal(doc.numero);
  const dia = diaFila(doc.fecha_publicacion, ahora) || (doc.year ? String(doc.year) : '');
  if (coleccion === 'jud') {
    return {
      a: tribunalDeRit(doc.numero)?.nombre || 'Sentencia',
      b: [n && `RIT ${n}`, dia].filter(Boolean).join(' · '),
      nota: null,
    };
  }
  return {
    a: [tipoSingular(doc.tipo), n].filter(Boolean).join(' '),
    b: dia,
    nota: coleccion === 'adm' && cambiaCriterio(doc) ? 'Cambia un criterio' : null,
  };
}

/** Señal de color de la fila, si la tiene: hoy solo «No vigente» (ámbar) en un convenio suscrito y no vigente. */
export function senalFila(doc: DocFila): { texto: string; tono: 'ambar' } | null {
  return esConvenio(doc) && estadoConvenio(doc).noVigente ? { texto: 'No vigente', tono: 'ambar' } : null;
}

export type Agrupacion = 'semana' | 'mes' | 'anio' | 'pais';

/** Agrupación de cada biblioteca: semana en el SII, mes en el TTA (§11.2), año en Leyes y país en Convenios (§11.6). */
export const AGRUPACION: Partial<Record<CollectionKey, Agrupacion>> = { adm: 'semana', jud: 'mes', ley: 'anio', conv: 'pais' };

/**
 * Rótulo del grupo de una fecha: «Semana del 13 de octubre» (lunes de esa semana) o «Septiembre de 2026». La semana
 * lleva el año solo si no es el en curso. Sin fecha, «Sin fecha».
 */
export function rotuloGrupo(fecha: string | null | undefined, modo: Agrupacion, ahora: Date = new Date()): string {
  const p = partes(fecha);
  if (!p) return 'Sin fecha';
  if (modo === 'anio' || modo === 'pais') return String(p.y);
  if (modo === 'mes') return `${MES_LARGO[p.m - 1].charAt(0).toUpperCase()}${MES_LARGO[p.m - 1].slice(1)} de ${p.y}`;
  const dia = new Date(Date.UTC(p.y, p.m - 1, p.d));
  const lunes = new Date(dia.getTime() - ((dia.getUTCDay() + 6) % 7) * 86400000);
  const y = lunes.getUTCFullYear();
  return `Semana del ${lunes.getUTCDate()} de ${MES_LARGO[lunes.getUTCMonth()]}${y === ahora.getFullYear() ? '' : ` de ${y}`}`;
}

/** Documentos consecutivos con el mismo rótulo, en el orden en que llegan (la lista ya viene ordenada por fecha). */
export function agrupar<T extends DocFila>(docs: T[], modo: Agrupacion, ahora: Date = new Date()): { rotulo: string; docs: T[] }[] {
  const grupos: { rotulo: string; docs: T[] }[] = [];
  for (const doc of docs) {
    const rotulo = modo === 'pais' ? paisConvenio(doc) || 'Sin país' : rotuloGrupo(doc.fecha_publicacion, modo, ahora);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.rotulo === rotulo) ultimo.docs.push(doc);
    else grupos.push({ rotulo, docs: [doc] });
  }
  return grupos;
}

// Normas citadas dentro de la frase (se dibujan en peso 500): artículos con su norma, documentos del SII, leyes y DL/DFL.
const ART = String.raw`\b(?:arts?\.|art[ií]culos?)\s*\d+(?:\s*[°º])?(?:\s+(?:bis|ter|qu[aá]ter))?`;
const SUB = String.raw`(?:,?\s*(?:N[°º]\s*\d+|n[úu]meros?\s+\d+|letras?\s+[a-zñ]\)?|inciso\s+(?:primero|segundo|tercero|cuarto|final|\d+[°º]?)))*`;
const NORMA = String.raw`(?:\s+(?:de\s+la\s+|del\s+)?(?:LIR|CT|LIVS|Ley\s+sobre\s+Impuesto\s+a\s+la\s+Renta|C[óo]digo\s+Tributario|D\.?\s?L\.?\s*(?:N[°º]\s*)?\d{3,4}|Ley\s+(?:N[°º]\s*)?\d{1,2}\.?\d{3})\b)?`;
const DOC = String.raw`\b(?:Oficio|Circular|Resoluci[óo]n(?:\s+Ex(?:enta|\.))?)\s+(?:N[°º]\s*)?\d+(?:\s+de\s+\d{4}|\/\d{4})?`;
const LEY = String.raw`\bLey\s+(?:N[°º]\s*)?\d{1,2}\.\d{3}`;
const DL = String.raw`(?:\bD\.\s?F?\.?\s?L\.|\bDF?L)\s*(?:N[°º]\s*)?\d{1,4}\b`;
const RE_NORMA = new RegExp(`${ART}${SUB}${NORMA}|${DOC}|${LEY}|${DL}`, 'gi');

export interface Tramo {
  texto: string;
  norma: boolean;
}

/** Corta la frase en tramos de texto y de norma citada. */
export function tramosNorma(texto: string): Tramo[] {
  const tramos: Tramo[] = [];
  let ultimo = 0;
  RE_NORMA.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = RE_NORMA.exec(texto)) !== null) {
    if (m[0].length === 0) { RE_NORMA.lastIndex++; continue; }
    if (m.index > ultimo) tramos.push({ texto: texto.slice(ultimo, m.index), norma: false });
    tramos.push({ texto: m[0], norma: true });
    ultimo = m.index + m[0].length;
  }
  if (ultimo < texto.length) tramos.push({ texto: texto.slice(ultimo), norma: false });
  return tramos;
}

/** Copia al portapapeles la cita del documento, con el formato de «Fuentes citadas» y el enlace a su página. */
export async function copiarCita(doc: DocFila): Promise<void> {
  if (!navigator.clipboard?.writeText) throw new Error('sin portapapeles');
  await navigator.clipboard.writeText(citaDocumento({
    ...doc, numero: doc.numero ?? '', year: doc.year ?? 0, fecha_publicacion: doc.fecha_publicacion ?? null, resumen: doc.resumen ?? '',
  }));
}
